import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import type { BookingSlot } from '@/types/scheduling.types';

interface SlotCardProps {
  slot: BookingSlot;
  timezone: string;
  isSelected: boolean;
  onSelect: (slotId: string) => void;
}

function formatTime(utcString: string, timezone: string): string {
  return new Date(utcString).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  });
}

function computeMinutes(startUtc: string, endUtc: string): number {
  return Math.round((new Date(endUtc).getTime() - new Date(startUtc).getTime()) / 60000);
}

export function SlotCard({ slot, timezone, isSelected, onSelect }: SlotCardProps) {
  const { t } = useTranslation();

  const startTime = formatTime(slot.startAtUtc, timezone);
  const endTime = formatTime(slot.endAtUtc, timezone);
  const duration = computeMinutes(slot.startAtUtc, slot.endAtUtc);

  return (
    <button
      type='button'
      onClick={() => onSelect(slot.slotId)}
      className={cn(
        'flex w-full items-center justify-between rounded-lg border px-4 py-3 text-left transition-colors',
        isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50',
      )}>
      <div>
        <span className='text-sm font-medium text-gray-900'>
          {startTime} – {endTime}
        </span>
        {slot.availableSpots > 1 && (
          <p className='text-xs text-gray-400'>{t('booking.spotsLeft', { count: slot.availableSpots })}</p>
        )}
      </div>

      <span className='rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500'>
        {t('booking.confirm.minutes', { count: duration })}
      </span>

      <div>
        {isSelected ? (
          <span className='inline-flex items-center gap-1 text-sm font-medium text-green-600'>
            <Check className='h-4 w-4' />
            {t('booking.selected')}
          </span>
        ) : (
          <span className='text-sm font-medium text-blue-600'>{t('booking.select')}</span>
        )}
      </div>
    </button>
  );
}
